// ─── Tagging people, groups and matters onto something ───────────────────────
//
// Search as you type, pick from what narrows, and what is already chosen sits
// above as removable chips.
//
// ── Where this came from, and why it moved ─────────────────────────────────
//
// Lifted verbatim (2026-09-28) from `@nulife/documents-ui`'s `UploadForm.tsx`,
// where it was a private function called `LinkPicker`. Markup, behaviour and
// every CSS rule are the ones Documents was already using; only the class
// prefix changed, from `nl-docs__` to `nl-pick__`, because a control shared by
// four screens cannot be named after one of them. The rules themselves were
// copied across character for character into `styles.css` — see the block
// there.
//
// Drea, 2026-09-28: the tag-person/group logic "must be a shared capability,
// built once and used by both Tasks and Proposals -- not two separate
// implementations", and the pattern to reuse is "the exact UI pattern already
// built for Documents' Tag People or Group". Both halves of that are met by
// moving the real component rather than by writing a second one that looks
// like it: Documents now renders THIS, so the three screens cannot drift.
//
// ── The two decisions inside it, carried over with their reasons ───────────
//
// **Nothing until something is typed** (Drea, 2026-09-27). It used to show the
// most recent candidates against an empty box, on the reasoning that the
// common case is tagging something just created. She corrected that: a list
// that appears before you have asked for anything is a list you have to
// dismiss, and on a phone it pushes the rest of the form off the screen.
//
// **Capped at eight.** The suggestion list has no `max-height` and no scroll,
// because a scrollbar inside a form is exactly what this control replaced — a
// bordered 220px box of forty checkboxes. The cap is what makes the absence of
// a scrollbar safe rather than lucky.

import { useState } from "react";

/** Something that can be tagged onto a thing: a person, a group, a matter.
 *  `group` is the heading it files under ("People", "Groups"), not a group in
 *  the membership sense — the word is the caller's, kept from Documents. */
export type PickTarget = {
  /** Unique across the whole list. The caller composes it; this control only
   *  compares it. */
  key: string;
  name: string;
  group?: string;
};

export function SubjectPicker({
  targets, chosen, onToggle, placeholder = "Type name", label = "Tag a person, group or matter",
  testIdPrefix = "pick", max = 8, browse = false, listAll = false, confirmRemove = false,
}: {
  targets: PickTarget[];
  /** The keys already on the thing. A set, so ticking and unticking is one
   *  operation and the order somebody picked them in cannot leak into what
   *  gets stored. */
  chosen: ReadonlySet<string>;
  onToggle: (key: string) => void;
  placeholder?: string;
  label?: string;
  /** So Documents keeps its own `documents-link*` test ids and the browser
   *  suite that names them does not have to be rewritten to prove a component
   *  moved. */
  testIdPrefix?: string;
  max?: number;
  /**
   * Offer the whole list before anything is typed, when it is short enough
   * to be worth offering.
   *
   * Off by default, so tagging is unchanged — see the note above about why
   * that list stays empty until asked for.
   *
   * On for picking a DOCUMENT to attach, which is a different question.
   * Drea, 2026-09-28: the attach list "offered NO options". The first cause
   * was a shadowed route; the second is this — her library holds one
   * document, and a box that shows nothing until you spell its name is no
   * use when you opened it to go and find out what is there. "Which of our
   * documents was it" is not "tag this person", the same distinction
   * Farndale draws with `browse` on its own recipient picker.
   *
   * Capped at `max`, and deliberately all-or-nothing: past the cap it shows
   * nothing and SAYS how many there are to search, because eight arbitrary
   * documents out of two hundred is not an answer to "which one", and a
   * silently truncated list reads as a complete one.
   */
  browse?: boolean;
  /**
   * The whole list, before anything is typed (Drea, 2026-10-05, for Tasks:
   * "adding a tag requires typing a character before the name list even
   * appears"). A committee is a few dozen names, so all of them, scrolling.
   * Opt-in: the earlier "nothing until typed" rule stands elsewhere until
   * she says otherwise.
   */
  listAll?: boolean;
  /**
   * A chosen name is just a name, with its own × that asks "Remove …?" before
   * anything happens (Drea, 2026-10-05: one accidental tap on a name untagged
   * somebody). Opt-in, as above.
   */
  confirmRemove?: boolean;
}) {
  const [text, setText] = useState("");
  const [removing, setRemoving] = useState<string | null>(null);

  const picked = targets.filter(t => chosen.has(t.key));
  const query = text.trim().toLowerCase();
  const offerable = targets.filter(t => !chosen.has(t.key));
  // Empty box, empty list — unless `browse` says otherwise and the list is
  // short enough to show whole. Not `|| !query`: that is what made it
  // pre-populate for everybody, which is the thing Drea corrected.
  const matches = !query
    ? (listAll ? offerable : browse && offerable.length <= max ? offerable : [])
    : offerable
        .filter(t => t.name.toLowerCase().includes(query)
          || (t.group ?? "").toLowerCase().includes(query))
        .slice(0, max);
  /** Browsing, but too many to list. Said out loud rather than shown as an
   *  empty box, which is indistinguishable from having nothing at all. */
  const tooManyToList = browse && !query && offerable.length > max;

  return (
    <div className="nl-pick" data-testid={`${testIdPrefix}-links`}>
      {picked.length > 0 && (
        <div className="nl-pick__chips" data-testid={`${testIdPrefix}-linked`}>
          {picked.map(t => confirmRemove ? (
            <span key={t.key} className="nl-pick__chip" data-testid={`${testIdPrefix}-link`}>
              {t.name}
              {t.group && <span className="nl-pick__chipGroup">{t.group}</span>}
              {removing === t.key ? (
                <span className="nl-pick__confirm" data-testid={`${testIdPrefix}-link-confirm`}>
                  Remove {t.name}?
                  <button type="button" className="nl-pick__confirmYes" data-testid={`${testIdPrefix}-link-confirm-yes`}
                          onClick={() => { setRemoving(null); onToggle(t.key); }}>Yes</button>
                  <button type="button" className="nl-pick__confirmNo"
                          onClick={() => setRemoving(null)}>No</button>
                </span>
              ) : (
                <button type="button" className="nl-pick__remove" aria-label={`Remove ${t.name}`}
                        data-testid={`${testIdPrefix}-link-remove`} onClick={() => setRemoving(t.key)}>×</button>
              )}
            </span>
          ) : (
            <button
              key={t.key}
              type="button"
              className="nl-pick__chip"
              data-testid={`${testIdPrefix}-link`}
              aria-label={`Remove ${t.name}`}
              onClick={() => onToggle(t.key)}
            >
              {t.name}
              {t.group && <span className="nl-pick__chipGroup">{t.group}</span>}
              <span aria-hidden>−</span>
            </button>
          ))}
        </div>
      )}

      <input
        className="nl-pick__field"
        value={text}
        placeholder={placeholder}
        aria-label={label}
        onChange={e => setText(e.target.value)}
        data-testid={`${testIdPrefix}-link-search`}
      />

      {matches.length > 0 && (
        <ul className="nl-pick__suggestions" data-testid={`${testIdPrefix}-link-suggestions`}
            style={listAll ? { maxHeight: 260, overflowY: "auto" } : undefined}>
          {matches.map(t => (
            <li key={t.key}>
              <button
                type="button"
                className="nl-pick__suggestion"
                data-testid={`${testIdPrefix}-link-suggestion`}
                onClick={() => { onToggle(t.key); setText(""); }}
              >
                {t.name}
                {t.group && <span className="nl-pick__chipGroup">{t.group}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
      {query && matches.length === 0 && (
        <p className="nl-pick__empty">Nothing matches “{text.trim()}”.</p>
      )}
      {tooManyToList && (
        <p className="nl-pick__empty" data-testid={`${testIdPrefix}-link-count`}>
          Type to find one of {offerable.length}.
        </p>
      )}
    </div>
  );
}
